import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ClientService, httpHeaders } from '../../client.service';

import {
  ChangeUserDetailsRequest,
  LoggedInUserDetails,
  UserDetails,
  UserRegistrationModel,
} from './user-registration.model';

export const removeEmptyParams = (obj: Object) => {
  let params = '';

  for (const key in obj) {
    if (!obj[key]) {
      delete obj[key];
    } else {
      params += `${key}=${encodeURIComponent(obj[key])}&`;
    }
  }

  return params.slice(0, -1);
};

@Injectable()
export class UserRegistrationApi {
  private readonly baseUrl = UserRegistrationModel.baseUrl;

  constructor(private clientService: ClientService) {}

  /**
   * Returns list of users
   * @param loginId
   * @param firstName
   * @param familyName
   * @param city
   * @param street
   * @param postalCode
   * @param limit
   * @param offset
   */
  listUsers(
    loginId: string,
    firstName: string,
    familyName: string,
    city: string,
    street: string,
    postalCode: string,
    limit: number,
    offset: number
  ): Observable<any> {
    const params = removeEmptyParams({
      loginId,
      'name.firstName': firstName,
      'name.familyName': familyName,
      'address.city': city,
      'address.street': street,
      'address.postalCode': postalCode,
    });
    const endpoint = `${this.baseUrl}/list?${params}&limit=${limit}&offset=${offset}`;

    return this.clientService.get(endpoint, {}, httpHeaders);
  }

  /**
   * Returns details of a single user
   * @param userId
   */
  getUserDetails(userId: string): Observable<UserDetails> {
    const endpoint = `${this.baseUrl}/${userId}`;

    return this.clientService.get(
      endpoint,
      {
        phoneNumberFormat: 'string',
        sections: ['identity', 'roles', 'sales'],
      },
      httpHeaders
    );
  }

  /**
   * Returns details of a logged in user
   */
  getLoggedInUserDetails(): Observable<LoggedInUserDetails> {
    const endpoint = UserRegistrationModel.meEndpointPath;

    return this.clientService.get(
      endpoint,
      {
        phoneNumberFormat: 'string',
        sections: ['basic', 'identity', 'iot', 'sales', 'roles'],
      },
      httpHeaders
    );
  }

  /**
   * Changes user details
   * @param userId
   * @param body
   */
  changeUserDetails(userId: string, body: ChangeUserDetailsRequest): Observable<any> {
    const endpoint = `${this.baseUrl}/${userId}`;

    return this.clientService.patch(endpoint, body);
  }

  /**
   * Resend user email confirmation
   * @param actionBody
   */
  resendUserEmailConfirmation(actionBody: any): Observable<any> {
    const endpoint = `${this.baseUrl}/send-email-confirmation`;

    return this.clientService.post(endpoint, actionBody);
  }

  /**
   * User email changed confirmation
   * @param actionBody
   */
  changedUserEmailConfirmation(actionBody: any): Observable<any> {
    const endpoint = `${this.baseUrl}/email-changed-confirmation`;

    return this.clientService.post(endpoint, actionBody);
  }

  /**
   * Reset user password
   * @param actionBody
   */
  resetUserPassword(actionBody: any): Observable<any> {
    const endpoint = `${this.baseUrl}/request-password-reset`;

    return this.clientService.post(endpoint, actionBody);
  }

  /**
   * Change user password
   * @param userId
   * @param actionBody
   */
  changeUserPassword(userId: string, actionBody: any): Observable<any> {
    const endpoint = `${this.baseUrl}/${userId}/password`;

    return this.clientService.patch(endpoint, actionBody);
  }
}
